import { createDataProvider } from "@refinedev/rest";
import type { CustomParams, HttpError } from "@refinedev/core";
import type { KyResponse } from "ky";
import { kyOptions } from "./utils";

const ensureTrailingSlash = (path: string): string => {
  if (!path) {
    return path;
  }
  const [pathname, query = ""] = path.split("?", 2);
  const normalizedPath = pathname.endsWith("/") ? pathname : `${pathname}/`;
  return query ? `${normalizedPath}?${query}` : normalizedPath;
};

const normalizeCustomUrl = (url: string): string => {
  if (!url) {
    return url;
  }

  // Only normalize API paths. Leave external URLs untouched.
  if (url.startsWith("/api/")) {
    return ensureTrailingSlash(url);
  }

  return url;
};

const normalizeResource = (resource: string): string => ensureTrailingSlash(resource);

const parseResponseBody = async (response: KyResponse): Promise<any> => {
  const text = await response.text();

  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch (error) {
    return text;
  }
};

// Create the data provider with CSRF and 401 handling
export const dataProvider = (apiUrl: string) => {
  const { dataProvider: provider } = createDataProvider(
    apiUrl,
    {
      // Custom method implementations for DRF compatibility
      getList: {
        getEndpoint: ({ resource }) => normalizeResource(resource),
        // Transform query parameters to match DRF expectations
        buildQueryParams: async ({ pagination, filters, sorters }) => {
          const params: Record<string, any> = {};

          // Handle pagination (DRF uses page and page_size)
          if (pagination) {
            const { currentPage = 1, pageSize = 10, mode = "server" } = pagination;
            if (mode === "server") {
              params.page = currentPage;
              params.page_size = pageSize;
            }
          }

          // Handle sorting (DRF uses ordering parameter)
          if (sorters && sorters.length > 0) {
            const ordering = sorters
              .map(sorter => {
                const prefix = sorter.order === "desc" ? "-" : "";
                return `${prefix}${sorter.field}`;
              })
              .join(",");
            params.ordering = ordering;
          }

          // Handle filters
          if (filters) {
            filters.forEach(filter => {
              if ('field' in filter && filter.value !== undefined && filter.value !== null) {
                // Handle different filter operators
                const operator = filter.operator || "eq";
                const fieldName = filter.field;

                switch (operator) {
                  case "contains":
                  case "containss":
                    params[`${fieldName}__icontains`] = filter.value;
                    break;
                  case "eq":
                    params[fieldName] = filter.value;
                    break;
                  case "ne":
                    params[`${fieldName}__ne`] = filter.value;
                    break;
                  case "gte":
                    params[`${fieldName}__gte`] = filter.value;
                    break;
                  case "lte":
                    params[`${fieldName}__lte`] = filter.value;
                    break;
                  case "gt":
                    params[`${fieldName}__gt`] = filter.value;
                    break;
                  case "lt":
                    params[`${fieldName}__lt`] = filter.value;
                    break;
                  case "in":
                    params[`${fieldName}__in`] = Array.isArray(filter.value)
                      ? filter.value.join(",")
                      : filter.value;
                    break;
                  default:
                    params[fieldName] = filter.value;
                }
              }
            });
          }

          return params;
        },
        // Transform DRF paginated response
        mapResponse: async (response) => {
          const data = await response.json() as any;

          // Check if it's a DRF paginated response
          if (data && typeof data === 'object' && 'results' in data && 'count' in data) {
            return data.results as any[];
          }

          // Fallback for non-paginated responses
          return (Array.isArray(data) ? data : [data]) as any[];
        },
        // Extract total count from DRF response
        getTotalCount: async (response) => {
          const data = await response.json() as any;

          // Check if it's a DRF paginated response
          if (data && typeof data === 'object' && 'count' in data) {
            return data.count as number;
          }

          // Fallback for non-paginated responses
          return (Array.isArray(data) ? data.length : 1) as number;
        },
      },
      getMany: {
        getEndpoint: ({ resource }) => normalizeResource(resource),
        // Transform DRF paginated response for getMany
        mapResponse: async (response) => {
          const data = await response.json() as any;

          // Check if it's a DRF paginated response
          if (data && typeof data === 'object' && 'results' in data) {
            return data.results as any[];
          }

          return (Array.isArray(data) ? data : [data]) as any[];
        },
      },
      getOne: {
        getEndpoint: ({ resource, id }) => `${normalizeResource(resource)}${id}/`,
      },
      create: {
        getEndpoint: ({ resource }) => normalizeResource(resource),
      },
      update: {
        getEndpoint: ({ resource, id }) => `${normalizeResource(resource)}${id}/`,
        getRequestMethod: () => "put",
      },
      deleteOne: {
        getEndpoint: ({ resource, id }) => `${normalizeResource(resource)}${id}/`,
      },
      custom: {
        buildQueryParams: async (params) => {
          return params.query ?? {};
        },
        buildHeaders: async (params) => {
          return params.headers ?? {};
        },
        buildBodyParams: async (params) => {
          return params.payload ?? {};
        },
        mapResponse: async (response) => {
          const data = await parseResponseBody(response);

          if (!response.ok) {
            const detailMessage = typeof data === 'object' && data !== null
              ? (data.detail || data.message)
              : undefined;

            const error: HttpError & { data?: unknown } = {
              message: detailMessage || response.statusText || 'Request failed',
              statusCode: response.status,
            };

            if (data !== null && data !== undefined) {
              error.data = data;
            }

            throw error;
          }

          return data;
        }
      }
    },
    kyOptions // Use shared KY options with CSRF and auth handling
  );

  const originalCustom = provider.custom?.bind(provider);
  if (originalCustom) {
    provider.custom = async (params: CustomParams) => {
      const nextParams: CustomParams = {
        ...params,
        url: normalizeCustomUrl(params.url),
      };
      return originalCustom(nextParams);
    };
  }

  return provider;
};
